import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowRight, X, Check, Shuffle, Star, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { BasketPanel } from "@/components/BasketPanel";
import { useAuth } from "@/context/AuthContext";
import { useCategories } from "@/context/SiteContentContext";
import { useBrand } from "@/context/PartnerContext";
import { gbp, tierUnitPrice, makeMixboxItem, boostMult, boostOptions, mixDiscountRate } from "@/lib/pricing";
import { groupFlavorsByRange } from "@/lib/flavorRanges";

const BOOST_CODES = ["standard", "x2", "x3"];
const boostShort = (c) => (c === "x2" ? "×2" : c === "x3" ? "×3" : "");
// High-contrast markers so mixed sizes in one box stand out at a glance (assigned smallest -> largest).
const SIZE_COLOURS = ["#facc15", "#22d3ee", "#a3e635", "#f472b6", "#fb923c", "#a78bfa"];
const mlLabel = (ml) => (ml >= 1000 ? `${ml / 1000}L` : `${ml}ml`);
const round2 = (n) => Math.round(n * 100) / 100;
const quantityAvailable = (size, quantity) => {
  const visible = size?.visible_quantities || size?.global_visible_quantities;
  if (!Array.isArray(visible)) return true;
  return new Set(visible.map(Number)).has(quantity <= 10 ? quantity : 11);
};

const VOL_TILES = [1000, 2000, 3000, 4000, 5000];
// Trade (dropship) accounts also get bulk 10L / 20L bundles at their own volume discount.
const TRADE_VOL_TILES = [10000, 20000];

// Preset multipacks per volume — each sums exactly to its volume and mixes 2+ bottle sizes
// (60ml / 100ml / 250ml / 500ml). parts = [[bottle_ml, qty], ...]. 6 options per volume.
const PRESETS = {
  1000: [
    { parts: [[250, 2], [100, 5]] },
    { parts: [[500, 1], [250, 2]] },
    { parts: [[500, 1], [100, 5]] },
    { parts: [[250, 2], [100, 2], [60, 5]] },
    { parts: [[100, 4], [60, 10]] },
    { parts: [[100, 7], [60, 5]] },
  ],
  2000: [
    { parts: [[500, 2], [250, 4]] },
    { parts: [[500, 1], [250, 4], [100, 5]] },
    { parts: [[250, 4], [100, 10]] },
    { parts: [[250, 4], [100, 4], [60, 10]] },
    { parts: [[100, 8], [60, 20]] },
    { parts: [[100, 14], [60, 10]] },
  ],
  3000: [
    { parts: [[500, 3], [250, 6]] },
    { parts: [[500, 2], [250, 4], [100, 10]] },
    { parts: [[250, 6], [100, 15]] },
    { parts: [[250, 6], [100, 6], [60, 15]] },
    { parts: [[100, 12], [60, 30]] },
    { parts: [[100, 21], [60, 15]] },
  ],
  4000: [
    { parts: [[500, 4], [250, 8]] },
    { parts: [[500, 2], [250, 8], [100, 10]] },
    { parts: [[250, 8], [100, 20]] },
    { parts: [[250, 8], [100, 8], [60, 20]] },
    { parts: [[100, 16], [60, 40]] },
    { parts: [[100, 28], [60, 20]] },
  ],
  5000: [
    { parts: [[500, 5], [250, 10]] },
    { parts: [[500, 2], [250, 10], [100, 15]] },
    { parts: [[250, 10], [100, 25]] },
    { parts: [[250, 10], [100, 10], [60, 25]] },
    { parts: [[100, 20], [60, 50]] },
    { parts: [[100, 35], [60, 25]] },
  ],
  10000: [
    { parts: [[500, 16], [250, 8]] },
    { parts: [[500, 12], [250, 16]] },
    { parts: [[500, 10], [250, 20]] },
    { parts: [[500, 14], [250, 4], [100, 10]] },
    { parts: [[500, 8], [250, 16], [100, 20]] },
    { parts: [[250, 24], [100, 40]] },
  ],
  20000: [
    { parts: [[500, 32], [250, 16]] },
    { parts: [[500, 24], [250, 32]] },
    { parts: [[500, 20], [250, 40]] },
    { parts: [[500, 28], [250, 8], [100, 20]] },
    { parts: [[500, 16], [250, 32], [100, 40]] },
    { parts: [[250, 48], [100, 80]] },
  ],
};

let uid = 0;
const newId = () => `b${Date.now()}_${uid++}`;

// Mixed-size multipacks are 5% cheaper than buying the same bottles separately.

export default function CustomOrderBuilder({ embedded = false, onProgress } = {}) {
  const cats = useCategories();
  const brand = useBrand();
  const { addItem, items, pricingRules } = useCart();
  const discountPct = Math.round((Number(pricingRules?.mix_discount_pct) || 0) * 10) / 10;
  const { user } = useAuth();
  const wholesale = !!(user && ["wholesale", "admin"].includes(user.role));
  const catalogKey = user ? `${user.id || user.email || "account"}:${user.role}` : "guest";
  const nav = useNavigate();
  const trade = user?.role === "dropship";
  const trade10 = Number(pricingRules?.trade_10l_discount_pct) || 0;
  const trade20 = Number(pricingRules?.trade_20l_discount_pct) || 0;
  const volTiles = trade ? [5000, ...TRADE_VOL_TILES] : VOL_TILES;
  const weightCap = trade ? 60000 : 20000;
  const rateFor = useCallback((ml) => {
    const base = mixDiscountRate();
    if (trade && ml >= 20000) return Math.max(base, trade20 / 100);
    if (trade && ml >= 10000) return Math.max(base, trade10 / 100);
    return base;
  }, [trade, trade10, trade20]);
  const discountPctFor = (ml) => Math.round(rateFor(ml) * 1000) / 10;
  const [savedBundles, setSavedBundles] = useState([]);
  const [loadedBundleName, setLoadedBundleName] = useState("");
  useEffect(() => {
    if (!trade) { setSavedBundles([]); return; }
    api.get("/me/saved-bundles").then((r) => setSavedBundles(r.data || [])).catch(() => {});
  }, [trade]);

  const [sizes, setSizes] = useState([]);
  const [flavors, setFlavors] = useState([]);
  const [bases, setBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [targetMl, setTargetMl] = useState(null);
  const [packKey, setPackKey] = useState(null);
  const [changingPack, setChangingPack] = useState(false);
  const [bottles, setBottles] = useState([]); // {id, sizeId, ml, flavorId, base, boost}

  const [applyBase, setApplyBase] = useState("CO");
  const [applyBoost, setApplyBoost] = useState("standard");

  const [picker, setPicker] = useState(null); // bottle id being edited
  const [draft, setDraft] = useState({ flavorId: "", base: "CO", boost: "standard" });
  const [sameAgainId, setSameAgainId] = useState(null); // bottle id showing the "add same again" overlay
  const [lowHint, setLowHint] = useState(null);
  const [completeModal, setCompleteModal] = useState(false);
  const wasComplete = useRef(false);
  const fillRef = useRef(null);
  const basketWeight = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.weight_g || 0) * Number(item.quantity || 1), 0),
    [items]
  );

  useEffect(() => {
    let current = true;
    setLoading(true);
    setLoadError("");
    setPackKey(null);
    setBottles([]);
    Promise.all([api.get("/sizes"), api.get("/flavors"), api.get("/bases")])
      .then(([s, f, b]) => {
        if (!current) return;
        const activeBases = b.data.filter((base) => base.active !== false);
        setSizes(s.data);
        setFlavors(f.data);
        setBases(activeBases);
        if (activeBases[0]) {
          setApplyBase(activeBases[0].code);
          setDraft((draft) => ({ ...draft, base: activeBases[0].code, boost: "standard" }));
        }
      })
      .catch(() => {
        if (!current) return;
        setLoadError("The deals could not be loaded. Please try again.");
        toast.error("The deals could not be loaded. Please try again.");
      })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [catalogKey]);

  const sizeByMl = useMemo(() => Object.fromEntries(sizes.map((s) => [s.ml, s])), [sizes]);
  const sizeById = useMemo(() => Object.fromEntries(sizes.map((s) => [s.id, s])), [sizes]);
  const flavorGroups = useMemo(() => {
    // Guest flavours are ordered only via the dedicated Guest Collection flow.
    const { dreamlabs } = groupFlavorsByRange(flavors);
    return [
      ...(dreamlabs.length ? [{ label: `${brand.name} Originals`, flavors: dreamlabs }] : []),
    ];
  }, [flavors, brand.name]);
  const tiersOf = useCallback(
    (size) => ((wholesale && size?.wholesale_tiers ? size.wholesale_tiers : size?.tiers) || []),
    [wholesale]
  );
  const priceOf = useCallback(
    (sizeId, qty = 1) => tierUnitPrice(tiersOf(sizeById[sizeId]), qty),
    [sizeById, tiersOf]
  );
  // Public retail unit price (trade accounts only) so we can show their saving vs standard.
  const retailOf = useCallback((sizeId, qty = 1) => {
    const s = sizeById[sizeId];
    const rt = (s?.retail_tiers && s.retail_tiers.length) ? s.retail_tiers : (s?.tiers || []);
    return tierUnitPrice(rt, qty);
  }, [sizeById]);
  const enabledStrengths = (baseCode) => {
    const enabled = bases.find((base) => base.code === baseCode)?.enabled_strengths;
    return Array.isArray(enabled) && enabled.length ? enabled : BOOST_CODES;
  };

  // Build the pack menu for the chosen volume with computed prices.
  // Only mixed-size packs (2+ distinct bottle sizes) are offered, priced 5% below buying separately.
  const packs = useMemo(() => {
    if (!targetMl || !sizes.length) return [];
    return (PRESETS[targetMl] || [])
      .map((p) => {
        const parts = p.parts
          .map(([ml, qty]) => ({ size: sizeByMl[ml], qty }))
          .sort((a, b) => Number(b.size?.ml || 0) - Number(a.size?.ml || 0));
        // Trade (dropship) accounts get bulk multipacks regardless of the per-size quantity grid gate.
        if (parts.some((part) => !part.size || (!trade && !quantityAvailable(part.size, part.qty)))) return null;
        const packWeight = parts.reduce((sum, part) => sum + Number(part.size.weight_g || 0) * part.qty, 0);
        if (basketWeight + packWeight > weightCap) return null;
        const separate = round2(parts.reduce((a, x) => a + priceOf(x.size.id, x.qty) * x.qty, 0));
        // Match cart and server rounding: discount each size's unit price first.
        const price = round2(parts.reduce(
          (total, part) => total + round2(priceOf(part.size.id, part.qty) * (1 - rateFor(targetMl))) * part.qty,
          0
        ));
        const count = parts.reduce((a, x) => a + x.qty, 0);
        const key = parts.map((x) => `${x.qty}x${x.size.ml}`).join("_");
        const retailSeparate = round2(parts.reduce((a, x) => a + retailOf(x.size.id, x.qty) * x.qty, 0));
        return { ...p, parts, separate, price, count, key, retailSeparate };
      })
      .filter((p) => p && p.parts.length >= 2);
  }, [targetMl, sizes, sizeByMl, priceOf, retailOf, basketWeight, rateFor, weightCap, trade]);

  const packLabel = (p) => p.parts.map((x) => `${x.qty} × ${mlLabel(x.size.ml)}`).join("  +  ");
  const selectedPack = packs.find((p) => p.key === packKey);

  const sizeCounts = useMemo(() => { const m = {}; bottles.forEach((b) => { m[b.sizeId] = (m[b.sizeId] || 0) + 1; }); return m; }, [bottles]);
  const unitFor = (sizeId) => round2(priceOf(sizeId, sizeCounts[sizeId] || 1) * (1 - rateFor(targetMl)));
  const subtotal = round2(bottles.reduce((a, b) => a + unitFor(b.sizeId) * boostMult(b.boost), 0));
  const totalVol = bottles.reduce((a, b) => a + (b.ml || 0), 0);
  const totalWeight = bottles.reduce((a, b) => a + Number(sizeById[b.sizeId]?.weight_g || 0), 0);
  const allHaveFlavour = bottles.length > 0 && bottles.every((b) => b.flavorId);
  const distinctMls = [...new Set(bottles.map((b) => b.ml || 0))].sort((a, b) => a - b);
  const sizeColor = (ml) => (distinctMls.length > 1 ? SIZE_COLOURS[distinctMls.indexOf(ml) % SIZE_COLOURS.length] : null);
  const boxFlavourCounts = useMemo(() => {
    const m = {};
    bottles.forEach((b) => { if (b.flavorId) m[b.flavorId] = (m[b.flavorId] || 0) + 1; });
    return Object.entries(m).map(([id, n]) => ({ flavor: flavors.find((f) => f.id === id), n })).filter((x) => x.flavor).sort((a, b) => b.n - a.n);
  }, [bottles, flavors]);

  useEffect(() => {
    if (allHaveFlavour && !wasComplete.current) { wasComplete.current = true; }
    if (!allHaveFlavour) { wasComplete.current = false; setCompleteModal(false); }
  }, [allHaveFlavour]);

  useEffect(() => { onProgress?.(bottles.length > 0); }, [bottles.length, onProgress]);

  // One-tap fill: spread varied in-stock flavours across every bottle (great for big 10L/20L bundles).
  const surpriseFill = () => {
    const pool = flavors.filter((f) => f.range_type !== "guest" && f.active !== false && (f.stock ?? 0) > 0);
    if (!pool.length) { toast.error("No flavours are in stock right now"); return; }
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    const baseCode = bases[0]?.code || "CO";
    setBottles((prev) => prev.map((b, i) => ({ ...b, flavorId: shuffled[i % shuffled.length].id, base: baseCode, boost: "standard" })));
    setSameAgainId(null); setPicker(null); setLowHint(null);
    toast.success("Filled with a surprise mix — tweak any bottle you like");
  };

  const saveUsualOrder = async () => {
    if (!allHaveFlavour) return;
    const name = window.prompt("Name this usual order (e.g. 'Weekly 10L mix')");
    if (!name || !name.trim()) return;
    const map = {};
    bottles.forEach((b) => {
      const k = `${b.sizeId}|${b.flavorId}|${b.base}|${b.boost}`;
      map[k] ||= { size_id: b.sizeId, flavor_id: b.flavorId, base: b.base, boost: b.boost, quantity: 0 };
      map[k].quantity += 1;
    });
    try {
      const { data } = await api.post("/me/saved-bundles", { name: name.trim(), target_ml: targetMl || totalVol, selections: Object.values(map) });
      setSavedBundles((prev) => [data, ...prev]);
      toast.success("Saved to your usual orders");
    } catch { toast.error("Could not save. Please try again."); }
  };

  const loadUsualOrder = (bundle) => {
    const list = [];
    (bundle.selections || []).forEach((s) => {
      const size = sizeById[s.size_id];
      if (!size) return;
      for (let i = 0; i < s.quantity; i += 1) list.push({ id: newId(), sizeId: s.size_id, ml: size.ml, flavorId: s.flavor_id, base: s.base, boost: s.boost });
    });
    if (!list.length) { toast.error("This saved order is no longer available"); return; }
    setTargetMl(bundle.target_ml || list.reduce((a, b) => a + (b.ml || 0), 0));
    setPackKey("saved");
    setChangingPack(false);
    setBottles(list);
    setLoadedBundleName(bundle.name);
    toast.success(`Loaded “${bundle.name}” — review and check out`);
    setTimeout(() => fillRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };

  const deleteUsualOrder = async (id) => {
    try { await api.delete(`/me/saved-bundles/${id}`); setSavedBundles((prev) => prev.filter((b) => b.id !== id)); }
    catch { toast.error("Could not delete. Please try again."); }
  };

  const sortBundles = (arr) => [...arr].sort((a, c) => (Number(!!c.pinned) - Number(!!a.pinned)) || (String(c.created_at) < String(a.created_at) ? -1 : 1));
  const pinUsualOrder = async (b) => {
    try {
      const { data } = await api.patch(`/me/saved-bundles/${b.id}`, { pinned: !b.pinned });
      setSavedBundles((prev) => sortBundles(prev.map((x) => (x.id === b.id ? data : x))));
    } catch { toast.error("Could not update. Please try again."); }
  };
  const renameUsualOrder = async (b) => {
    const name = window.prompt("Rename this usual order", b.name);
    if (!name || !name.trim() || name.trim() === b.name) return;
    try {
      const { data } = await api.patch(`/me/saved-bundles/${b.id}`, { name: name.trim() });
      setSavedBundles((prev) => prev.map((x) => (x.id === b.id ? data : x)));
      toast.success("Renamed");
    } catch { toast.error("Could not rename. Please try again."); }
  };

  const checkoutUsualOrder = (bundle) => {
    const groups = {};
    (bundle.selections || []).forEach((s) => {
      const size = sizeById[s.size_id];
      if (!size) return;
      (groups[s.size_id] ||= { size, sel: {} });
      const fl = flavors.find((f) => f.id === s.flavor_id);
      const k = `${s.flavor_id}-${s.base}-${s.boost}`;
      groups[s.size_id].sel[k] ||= { flavor_id: s.flavor_id, flavor_name: fl?.name || "", base: s.base, boost: s.boost, quantity: 0 };
      groups[s.size_id].sel[k].quantity += s.quantity;
    });
    const entries = Object.values(groups);
    if (!entries.length) { toast.error("This saved order is no longer available"); return; }
    entries.forEach(({ size, sel }) => addItem(makeMixboxItem(size, Object.values(sel), { tiers: tiersOf(size), isWholesale: wholesale })));
    toast.success(`Added “${bundle.name}” to your basket`);
    nav("/checkout");
  };

  const pickVolume = (ml) => { setTargetMl(ml); setPackKey(null); setChangingPack(false); setBottles([]); setSameAgainId(null); setLowHint(null); setLoadedBundleName(""); };
  const choosePack = (p) => {
    setPackKey(p.key);
    setChangingPack(false);
    setSameAgainId(null);
    setLoadedBundleName("");
    const list = [];
    p.parts.forEach((x) => { for (let i = 0; i < x.qty; i += 1) list.push({ id: newId(), sizeId: x.size.id, ml: x.size.ml, flavorId: "", base: bases[0]?.code || "CO", boost: "standard" }); });
    setBottles(list);
    setTimeout(() => fillRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };

  const openEdit = (b) => { setSameAgainId(null); setDraft({ flavorId: b.flavorId, base: b.flavorId ? b.base : applyBase, boost: b.flavorId ? b.boost : applyBoost }); setPicker(b.id); };
  const clearBottle = (id) => { setSameAgainId(null); setBottles((prev) => prev.map((b) => (b.id === id ? { ...b, flavorId: "", boost: "standard" } : b))); };
  // Tapping a flavour auto-fills the bottle (using the base/strength selected); if empties remain, show the "same again" overlay on this tile.
  const pickFlavour = (flavorId) => {
    const choice = { flavorId, base: draft.base, boost: draft.boost };
    const editedId = picker;
    setBottles((prev) => prev.map((b) => (b.id === editedId ? { ...b, ...choice } : b)));
    setPicker(null);
    setSameAgainId(bottles.some((b) => b.id !== editedId && !b.flavorId) ? editedId : null);
    const f = flavors.find((x) => x.id === flavorId);
    const thr = f?.low_stock_threshold ?? 10;
    if (f && f.stock > 0 && f.stock <= thr) {
      const alt = flavors.filter((x) => x.id !== f.id && x.range_type !== "guest" && x.stock > (x.low_stock_threshold ?? 10)).sort((a, b) => b.stock - a.stock)[0];
      setLowHint(alt ? { bottleId: editedId, flavor: f, alt } : null);
    } else {
      setLowHint(null);
    }
  };
  const swapLowHint = () => {
    if (!lowHint) return;
    setBottles((prev) => prev.map((b) => (b.id === lowHint.bottleId ? { ...b, flavorId: lowHint.alt.id } : b)));
    toast.success(`Swapped to ${lowHint.alt.name}`);
    setLowHint(null);
  };
  // Copy the prompted tile's selection into the next n empty bottles; move the overlay onto the last one filled.
  const addSameAgain = (n = 1) => {
    setBottles((prev) => {
      const src = prev.find((b) => b.id === sameAgainId);
      if (!src) { setSameAgainId(null); return prev; }
      const choice = { flavorId: src.flavorId, base: src.base, boost: src.boost };
      let updated = [...prev];
      let lastId = null;
      for (let f = 0; f < n; f += 1) {
        const nextEmpty = updated.find((b) => !b.flavorId);
        if (!nextEmpty) break;
        updated = updated.map((b) => (b.id === nextEmpty.id ? { ...b, ...choice } : b));
        lastId = nextEmpty.id;
      }
      setSameAgainId(lastId && updated.some((b) => !b.flavorId) ? lastId : null);
      return updated;
    });
  };

  const addToOrder = () => {
    if (!allHaveFlavour) { toast.error("Please choose a flavour for every bottle"); return false; }
    if (basketWeight + totalWeight > weightCap) { toast.error(`Online orders are limited to ${weightCap / 1000}kg`); return false; }
    const groups = {};
    bottles.forEach((b) => { (groups[b.sizeId] ||= []).push(b); });
    Object.entries(groups).forEach(([sizeId, list]) => {
      const size = sizeById[sizeId];
      const sel = {};
      list.forEach((b) => {
        const fl = flavors.find((f) => f.id === b.flavorId);
        const k = `${b.flavorId}-${b.base}-${b.boost}`;
        sel[k] ||= { flavor_id: b.flavorId, flavor_name: fl?.name || "", base: b.base, boost: b.boost, quantity: 0 };
        sel[k].quantity += 1;
      });
      addItem(makeMixboxItem(size, Object.values(sel), { tiers: tiersOf(size), isWholesale: wholesale }));
    });
    toast.success("Multipack Deal added to your basket");
    setPackKey(null); setBottles([]); setPicker(null); setSameAgainId(null); setLowHint(null); setCompleteModal(false); wasComplete.current = false;
    return true;
  };
  const addAndCheckout = () => { if (addToOrder()) nav("/checkout"); };

  const editingBottle = bottles.find((b) => b.id === picker);

  if (loading || loadError) {
    return (
      <div className="mx-auto flex min-h-[45vh] max-w-[900px] flex-col items-center justify-center px-6 text-center">
        <div className="font-heading text-3xl font-black">{cats.deals.title}</div>
        <p className="mt-2 max-w-lg text-sm text-muted-foreground">
          {loading ? "Loading the familiar mixed-size deals…" : loadError}
        </p>
      </div>
    );
  }

  return (
    <div className={`mx-auto max-w-[1300px] px-6 ${embedded ? "pt-2" : "pt-8"} pb-28 lg:pb-12`} data-testid="custom-order-page">
      {!embedded && (
        <>
          <div className="mono text-[11px] uppercase tracking-[0.25em] text-primary">Build it your way</div>
          <h1 className="mt-1 font-heading text-3xl font-black tracking-tighter sm:text-4xl">{cats.deals.title}</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Choose your total volume, pick a multipack, then fill each bottle with a flavour, base and strength.</p>
        </>
      )}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_340px]">
        <div>
          {/* ONE panel: total volume -> multipack -> (opens in place) fill each bottle */}
          <div ref={fillRef} className={`border bg-card p-4 scroll-mt-24 transition-colors ${bottles.length === 0 || allHaveFlavour ? "border-border" : "border-primary/50 ring-1 ring-primary/20"}`} data-testid="step-fill">
            {trade && savedBundles.length > 0 && (
              <div className="mb-4 border border-primary/30 bg-primary/5 p-3" data-testid="usual-orders">
                <div className="mono text-[10px] uppercase tracking-[0.2em] text-primary">Your usual orders — one tap to reload</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {savedBundles.map((b) => (
                    <div key={b.id} className="flex items-center gap-1 border border-border bg-background pl-2">
                      <button onClick={() => pinUsualOrder(b)} data-testid={`pin-bundle-${b.id}`} title={b.pinned ? "Unpin" : "Pin to top"} className={`py-1.5 ${b.pinned ? "text-primary" : "text-muted-foreground hover:text-primary"}`}><Star className="h-3 w-3" fill={b.pinned ? "currentColor" : "none"} /></button>
                      <button onClick={() => loadUsualOrder(b)} data-testid={`load-bundle-${b.id}`} className="py-1.5 pl-1 text-xs font-medium hover:text-primary">{b.name} · {mlLabel(b.target_ml)}</button>
                      <button onClick={() => renameUsualOrder(b)} data-testid={`rename-bundle-${b.id}`} title="Rename" className="border-l border-border px-2 py-1.5 text-muted-foreground hover:text-foreground"><Pencil className="h-3 w-3" /></button>
                      <button onClick={() => checkoutUsualOrder(b)} data-testid={`checkout-bundle-${b.id}`} title="Add to basket & checkout" className="border-l border-border px-2 py-1.5 text-primary hover:bg-primary hover:text-primary-foreground"><ArrowRight className="h-3 w-3" /></button>
                      <button onClick={() => deleteUsualOrder(b.id)} data-testid={`delete-bundle-${b.id}`} aria-label={`Delete ${b.name}`} className="border-l border-border px-2 py-1.5 text-muted-foreground hover:text-red-600"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!targetMl ? (
              <div data-testid="step-volume">
                <div className="flex items-center gap-2.5">
                  <span className="mono flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-black text-primary-foreground">1</span>
                  <span className="font-heading text-xl font-black tracking-tight">How much do you want in total?</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Pick a total volume and the multipacks for it open up right here — {trade ? `up to ${Math.max(discountPct, trade20)}% off at 10L & 20L` : `${discountPct}% off`} versus buying the sizes separately.</p>
                <div className="mt-3 grid grid-cols-5 gap-2" data-testid="volume-tiles">
                  {volTiles.map((v) => (
                    <button key={v} onClick={() => pickVolume(v)} data-testid={`vol-${v}`}
                      className="flex flex-col items-center border border-border py-4 transition-all hover:-translate-y-0.5 hover:border-primary">
                      <span className="font-heading text-2xl font-black tracking-tighter">{mlLabel(v)}</span>
                      <span className="mono text-[9px] uppercase tracking-[0.15em] opacity-70">total</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {/* Row 1 — total volume chips (switch any time) */}
                <div className="flex flex-wrap items-center gap-2" data-testid="volume-tiles">
                  <span className="mono mr-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total volume</span>
                  <div className="flex flex-wrap gap-1.5">
                    {volTiles.map((v) => (
                      <button key={v} onClick={() => pickVolume(v)} data-testid={`vol-${v}`}
                        className={`mono border px-3 py-1.5 text-[11px] font-bold transition-colors ${targetMl === v ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>
                        {mlLabel(v)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Row 2 — multipack: cards until chosen, then a one-line summary with Change */}
                <div className="mt-4 border-t border-border pt-4" data-testid="step-pack">
                  {(!packKey || changingPack) ? (
                    <>
                      <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Choose your {mlLabel(targetMl)} multipack</div>
                      <p className="mt-1 text-xs text-muted-foreground">Every pack adds up to exactly {mlLabel(targetMl)}. Prices at standard strength — add ×2/×3 per bottle next.</p>
                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="pack-list">
                    {packs.map((p) => {
                      const active = packKey === p.key;
                      return (
                        <button key={p.key} onClick={() => choosePack(p)} data-testid={`pack-${p.key}`}
                          className={`flex items-center justify-between gap-3 border px-4 py-3 text-left transition-colors ${active ? "border-primary bg-primary/10" : "border-border hover:border-primary"}`}>
                          <div className="min-w-0">
                            <span className="mono mb-1 inline-block rounded-full bg-primary/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-primary">{discountPctFor(targetMl)}% off</span>
                            <div className="truncate text-sm font-medium">{packLabel(p)}</div>
                            <div className="mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{p.count} bottles · mixed sizes</div>
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            <div className="text-right leading-tight">
                              <div className="mono text-[10px] text-muted-foreground line-through">{gbp(p.separate)}</div>
                              <span className="mono text-sm font-bold text-primary">{gbp(p.price)}</span>
                              {p.separate > p.price && <div className="mono text-[9px] font-bold text-green-600" data-testid={`pack-save-${p.key}`}>save {gbp(round2(p.separate - p.price))}</div>}
                              {trade && p.retailSeparate > p.price && <div className="mono text-[8px] uppercase tracking-[0.1em] text-muted-foreground">vs {gbp(p.retailSeparate)} retail</div>}
                            </div>
                            {active && <Check className="h-4 w-4 text-primary" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {packs.length === 0 && (
                    <p className="mt-3 border border-dashed border-border p-3 text-sm text-muted-foreground" data-testid="no-packs-available">
                      No {cats.deals.title} are available for this volume with your current basket and pricing access.
                    </p>
                  )}
                    </>
                  ) : (
                    <div className="flex items-center justify-between gap-3" data-testid="pack-selected">
                      <div className="min-w-0">
                        <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{mlLabel(targetMl)} {loadedBundleName ? "usual order" : "multipack"}{selectedPack?.tag ? ` · ${selectedPack.tag}` : ""}</div>
                        <div className="truncate text-sm font-medium">{selectedPack ? packLabel(selectedPack) : (loadedBundleName || "Your saved order")}</div>
                        <div className="mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{selectedPack?.count ?? bottles.length} bottles · {gbp(selectedPack?.price ?? subtotal)}</div>
                        {selectedPack && selectedPack.separate > selectedPack.price && (
                          <div className="mono mt-0.5 text-[10px] font-bold text-green-600" data-testid="pack-selected-save">You save {gbp(round2(selectedPack.separate - selectedPack.price))} vs buying separately</div>
                        )}
                        {trade && selectedPack && selectedPack.retailSeparate > selectedPack.price && (
                          <div className="mono mt-0.5 text-[10px] text-muted-foreground" data-testid="pack-selected-retail-save">That's {gbp(round2(selectedPack.retailSeparate - selectedPack.price))} less than standard retail</div>
                        )}
                      </div>
                      <button onClick={() => setChangingPack(true)} data-testid="change-pack"
                        className="mono shrink-0 border border-border px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] transition-colors hover:border-primary hover:text-primary">Change</button>
                    </div>
                  )}
                </div>

                {/* Row 3 — fill each bottle */}
                {bottles.length > 0 && (
                  <div className="mt-4 border-t border-border pt-4">
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                      <div className="flex items-center gap-2.5">
                        <span className="font-heading text-lg font-black tracking-tight">Fill each bottle</span>
                        <span className="mono text-[11px] text-muted-foreground" data-testid="cob-fill-progress">{bottles.filter((b) => b.flavorId).length}/{bottles.length} filled</span>
                      </div>
                      <button type="button" onClick={surpriseFill} data-testid="surprise-fill"
                        className="mono order-3 ml-auto inline-flex items-center gap-1.5 border border-primary px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-primary transition-transform hover:-translate-y-0.5">
                        <Shuffle className="h-3.5 w-3.5" /> Surprise fill
                      </button>
                      {trade && allHaveFlavour && (
                        <button type="button" onClick={saveUsualOrder} data-testid="save-usual-order"
                          className="mono order-4 inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] transition-colors hover:border-primary hover:text-primary">
                          <Star className="h-3.5 w-3.5" /> Save as usual
                        </button>
                      )}
                      {distinctMls.length > 1 && (
                        <div className="flex flex-wrap items-center gap-1.5" data-testid="size-legend">
                          <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Sizes</span>
                          {distinctMls.map((ml) => (
                            <span key={ml} className="mono inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-[10px] font-bold" data-testid={`size-legend-${ml}`}>
                              <span className="h-2.5 w-2.5 rounded-full" style={{ background: sizeColor(ml) }} />
                              {mlLabel(ml)} × {bottles.filter((b) => b.ml === ml).length}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">Tap any bottle to choose its flavour — pick the base &amp; strength for each bottle inside the picker.</p>
                    <AnimatePresence>
                      {lowHint && (
                        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                          className="mt-2 flex flex-wrap items-center gap-2 border border-yellow-500/40 bg-yellow-500/10 px-3 py-2 text-xs" data-testid="low-stock-hint">
                          <span className="text-yellow-700 dark:text-yellow-500">Heads up — <b>{lowHint.flavor.name}</b> is running low ({lowHint.flavor.stock} left). Prefer a fully-stocked pick?</span>
                          <button type="button" onClick={swapLowHint} data-testid="low-stock-hint-swap"
                            className="mono border border-primary bg-primary px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-primary-foreground transition-transform hover:-translate-y-0.5">
                            Use {lowHint.alt.name}
                          </button>
                          <button type="button" onClick={() => setLowHint(null)} data-testid="low-stock-hint-dismiss" className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <div className="mt-3">
                  <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6 lg:grid-cols-8" data-testid="tile-grid">
                    {bottles.map((b, i) => {
                      const fl = flavors.find((f) => f.id === b.flavorId);
                      const sc = sizeColor(b.ml);
                      return (
                        <div key={b.id} onClick={() => openEdit(b)} data-testid={`tile-${i}`}
                          className={`relative flex aspect-square cursor-pointer flex-col overflow-hidden border transition-transform hover:-translate-y-0.5 ${fl ? "" : "choose-flavour-pulse border-dashed"}`}
                          style={fl ? { borderColor: fl.accent_color } : sc ? { borderColor: sc, borderWidth: 2 } : undefined}>
                          {!fl && <span className="choose-flavour-sheen" aria-hidden="true" />}
                          {fl && sameAgainId === b.id && (
                            <div data-testid={`same-again-${i}`} onClick={(e) => e.stopPropagation()}
                              className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-1 bg-primary/60 text-primary-foreground backdrop-blur-[2px]">
                              <span className="mono text-center text-[8px] font-bold uppercase leading-tight tracking-widest">Same again</span>
                              <div className="flex flex-wrap justify-center gap-1">
                                <button type="button" onClick={(e) => { e.stopPropagation(); addSameAgain(1); }} data-testid={`same-again-1-${i}`}
                                  className="mono rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-black text-primary transition-colors hover:bg-white">+1</button>
                                {bottles.filter((x) => !x.flavorId).length > 1 && (
                                  <button type="button" onClick={(e) => { e.stopPropagation(); addSameAgain(5); }} data-testid={`same-again-5-${i}`}
                                    className="mono rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-black text-primary transition-colors hover:bg-white">+5</button>
                                )}
                                {bottles.filter((x) => !x.flavorId).length > 1 && (
                                  <button type="button" onClick={(e) => { e.stopPropagation(); addSameAgain(bottles.filter((x) => !x.flavorId).length); }} data-testid={`same-again-all-${i}`}
                                    className="mono rounded bg-white px-1.5 py-0.5 text-[10px] font-black text-primary ring-1 ring-primary transition-colors hover:bg-primary hover:text-primary-foreground">+All</button>
                                )}
                              </div>
                            </div>
                          )}
                          <div className="relative min-h-0 flex-1 bg-white">
                            {fl?.image_url ? <img src={fl.image_url} alt={fl.name} className="h-full w-full object-cover" />
                              : fl ? <div className="h-full w-full" style={{ background: fl.accent_color }} />
                              : <div className="flex h-full w-full flex-col items-center justify-center gap-0.5 bg-muted"><Plus className="h-4 w-4 text-primary" /><span className="mono text-[8px] font-bold uppercase leading-none tracking-[0.1em] text-primary">Choose flavour</span></div>}
                            <span className="mono absolute left-1 top-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold" style={sc ? { background: sc, color: "#111" } : { background: "rgba(0,0,0,0.7)", color: "#fff" }} data-testid={`tile-size-${i}`}>{mlLabel(b.ml)}</span>
                            {b.boost !== "standard" && <span className="mono absolute bottom-1 left-1 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold text-primary-foreground" data-testid={`tile-boost-${i}`}>{boostShort(b.boost)}</span>}
                          </div>
                          {fl && (
                            <button onClick={(e) => { e.stopPropagation(); clearBottle(b.id); }} data-testid={`tile-remove-${i}`}
                              className="absolute right-1 top-1 z-30 rounded-full bg-black/50 p-0.5 text-white transition-colors hover:bg-black/80"><X className="h-3 w-3" /></button>
                          )}
                          {fl && (
                            <div className="flex items-center justify-between gap-1 px-1.5 py-1" style={{ background: fl.accent_color }}>
                              <span className="truncate text-[9px] font-bold leading-tight text-white drop-shadow">{fl.name}</span>
                              <span className="mono shrink-0 rounded-full bg-black/40 px-1.5 py-0.5 text-[8px] font-bold text-white">{b.base}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-24 h-fit" data-testid="cob-summary">
          <div className="border border-border bg-card p-5">
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Your Multipack Deal</div>
            {!targetMl ? (
              <p className="mt-3 text-sm text-muted-foreground">Choose a total volume to begin.</p>
            ) : bottles.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">{mlLabel(targetMl)} selected — now pick a multipack.</p>
            ) : (
              <>
                <div className="mt-2 font-heading text-2xl font-black tracking-tighter" data-testid="cob-heading">{mlLabel(totalVol)} · {bottles.length} bottle{bottles.length === 1 ? "" : "s"}</div>
                <div className="mono mt-1 text-[11px] text-muted-foreground" data-testid="cob-fill-status">{allHaveFlavour ? "All bottles flavoured ✓" : `${bottles.filter((b) => b.flavorId).length}/${bottles.length} flavoured`}</div>
                <div className="mt-4 border-t border-border pt-4">
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="mono font-bold" data-testid="cob-subtotal">{gbp(subtotal)}</span></div>
                  <div className="mt-3 flex items-end justify-between border-t border-border pt-3">
                    <span className="text-sm font-semibold">Total</span>
                    <span className="font-heading text-3xl font-black text-primary mono" data-testid="cob-total">{gbp(subtotal)}</span>
                  </div>
                  <div className="mono mt-1 text-[10px] text-muted-foreground">before shipping · crypto only</div>
                </div>
                {allHaveFlavour ? (
                  <>
                    <button onClick={addAndCheckout} data-testid="cob-add-and-checkout"
                      className="mt-4 flex w-full items-center justify-center gap-2 bg-primary py-4 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5">
                      Checkout with this pack — {gbp(subtotal)} <ArrowRight className="h-4 w-4" />
                    </button>
                    <button onClick={addToOrder} data-testid="cob-add-to-order"
                      className="mono mt-2 flex w-full items-center justify-center gap-2 border border-border py-3 text-[11px] font-bold uppercase tracking-[0.15em] transition-colors hover:border-primary hover:text-primary">
                      <Plus className="h-3.5 w-3.5" /> Add pack &amp; build another
                    </button>
                  </>
                ) : (
                  <>
                    <button disabled data-testid="cob-add-to-order"
                      className="mt-4 flex w-full items-center justify-center gap-2 bg-primary py-4 text-sm font-semibold text-primary-foreground opacity-40">
                      <Plus className="h-4 w-4" /> Add to Order
                    </button>
                    <p className="mono mt-2 text-center text-[11px] text-muted-foreground" data-testid="cob-blocked">Choose a flavour for every bottle to continue</p>
                  </>
                )}
            </>
            )}
            <BasketPanel />
          </div>
        </aside>
      </div>

      {bottles.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-primary/40 bg-background/95 px-4 py-3 backdrop-blur lg:hidden" data-testid="cob-mobile-bar">
          <div className="mx-auto flex max-w-[1300px] items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-heading text-xl font-black text-primary mono">{gbp(subtotal)}</div>
              <div className="mono truncate text-[10px] text-muted-foreground">{mlLabel(totalVol)} · {allHaveFlavour ? "all bottles flavoured" : `${bottles.filter((b) => b.flavorId).length}/${bottles.length} flavoured`}</div>
            </div>
            <button onClick={addToOrder} disabled={!allHaveFlavour} data-testid="cob-mobile-add-to-order"
              className="flex shrink-0 items-center gap-2 bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-40">
              <Plus className="h-4 w-4" /> Add to basket
            </button>
          </div>
        </div>
      )}

      {/* Box complete — blocking prompt */}
      <AnimatePresence>
        {completeModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" data-testid="box-complete-modal-backdrop">
            <motion.div initial={{ opacity: 0, scale: 0.94, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 6 }} transition={{ duration: 0.25 }}
              className="w-[min(26rem,calc(100vw-2rem))] border border-primary bg-card p-6 shadow-2xl shadow-black/60" data-testid="box-complete-modal" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2 text-primary">
                <Check className="h-5 w-5" />
                <span className="mono text-[11px] uppercase tracking-[0.2em]">Order ready</span>
              </div>
              <div className="mt-2 font-heading text-2xl font-black tracking-tight">Your {mlLabel(totalVol)} order is full</div>
              <div className="mono mt-1 text-sm text-muted-foreground">{bottles.length} bottle{bottles.length === 1 ? "" : "s"} · <span className="font-bold text-primary">{gbp(subtotal)}</span></div>

              <div className="mono mt-4 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Your mix · {boxFlavourCounts.length} flavour{boxFlavourCounts.length === 1 ? "" : "s"}</div>
              <div className="mt-2 flex max-h-24 flex-wrap gap-1.5 overflow-y-auto" data-testid="modal-flavor-strip">
                {boxFlavourCounts.map(({ flavor: f, n }) => (
                  <div key={f.id} title={`${n} × ${f.name}`} data-testid={`modal-flavor-${f.id}`}
                    className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full border border-border bg-white">
                    {f.image_url ? <img src={f.image_url} alt={f.name} className="h-full w-full object-cover" /> : <span className="block h-full w-full" style={{ background: f.accent_color }} />}
                    <span className="mono absolute bottom-0 right-0 rounded-tl-md bg-black/75 px-1 text-[8px] font-bold leading-tight text-white">{n}</span>
                  </div>
                ))}
              </div>

              <button onClick={addToOrder} data-testid="modal-add-box"
                className="mt-5 flex w-full items-center justify-center gap-2 bg-primary py-4 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5">
                <Plus className="h-4 w-4" /> Add to Order — {gbp(subtotal)} <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={() => setCompleteModal(false)} data-testid="modal-keep-editing"
                className="mono mt-3 w-full text-center text-[11px] uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-primary">
                Keep editing bottles
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Picker */}
      {picker && editingBottle && (
        <>
          <div className="fixed inset-0 z-40 bg-black/60" onClick={() => setPicker(null)} data-testid="picker-backdrop" />
          <div className="fixed left-1/2 top-1/2 z-50 w-[min(30rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 border border-border bg-card p-4 shadow-2xl shadow-black/60" data-testid="bottle-picker" onClick={(e) => e.stopPropagation()}>
            <div className="mono mb-3 flex items-center justify-between text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              <span>{mlLabel(editingBottle.ml)} bottle · {gbp(round2(unitFor(editingBottle.sizeId) * boostMult(draft.boost)))}</span>
              <button onClick={() => setPicker(null)} data-testid="picker-close" className="rounded-full p-0.5 hover:bg-muted"><X className="h-4 w-4" /></button>
            </div>

            <div className="mono mb-1.5 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Base</div>
            <div className="mb-1 flex gap-1">
              {bases.map((b) => (
                <button key={b.code} onClick={() => setDraft((d) => ({ ...d, base: b.code, boost: enabledStrengths(b.code).includes(d.boost) ? d.boost : "standard" }))} data-testid={`picker-base-${b.code}`} title={b.description || undefined}
                  className={`flex-1 border px-2 py-1.5 text-xs font-medium transition-colors ${draft.base === b.code ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>{b.name || b.code}</button>
              ))}
            </div>
            {bases.find((b) => b.code === draft.base)?.description && (
              <p className="mb-2 text-[10px] leading-snug text-muted-foreground" data-testid="picker-base-description">{bases.find((b) => b.code === draft.base).description}</p>
            )}
            <div className="mb-2" />

            <div className="mono mb-1.5 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Strength</div>
            <div className="mb-3 flex gap-1" data-testid="picker-boost-row">
              {boostOptions().filter((b) => enabledStrengths(draft.base).includes(b.code)).map((b) => (
                <button key={b.code} onClick={() => setDraft((d) => ({ ...d, boost: b.code }))} data-testid={`picker-boost-${b.code}`}
                  className={`flex flex-1 flex-col items-center border px-1 py-1.5 text-xs font-medium transition-colors ${draft.boost === b.code ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>
                  <span title={b.note}>{b.short}</span>
                  <span className="mono text-[9px] opacity-80">{`+${gbp(b.mult === 1 ? 0 : round2(unitFor(editingBottle.sizeId) * (b.mult - 1)))}`}</span>
                </button>
              ))}
            </div>

            <div className="mono mb-1.5 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Flavour <span className="opacity-60">· tap to fill</span></div>
            <div className="max-h-[45vh] space-y-3 overflow-y-auto pr-1" data-testid="picker-flavor-grid">
              {flavorGroups.map((group) => (
                <section key={group.label} aria-label={group.label} data-testid="picker-flavor-group">
                  <div className="mono mb-1.5 text-[9px] font-bold uppercase tracking-[0.16em] text-primary">{group.label}</div>
                  <div className="grid grid-cols-5 gap-1.5">
                    {group.flavors.map((f) => (
                      <button key={f.id} disabled={f.stock <= 0} data-testid={`picker-flavor-${f.id}`}
                        onClick={() => pickFlavour(f.id)}
                        className={`flex flex-col items-center gap-1 border p-1.5 text-center transition-colors hover:border-primary hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 disabled:grayscale ${editingBottle.flavorId === f.id ? "border-primary bg-muted" : "border-border"}`}>
                        <span className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-border bg-white">
                          {f.image_url ? <img src={f.image_url} alt="" className="h-full w-full object-cover" /> : <span className="block h-full w-full rounded-full" style={{ background: f.accent_color }} />}
                        </span>
                        <span className="text-[9px] font-medium leading-tight">{f.name}</span>
                        {f.stock <= 0 && <span className="mono text-[8px] font-bold uppercase tracking-[0.1em] text-destructive" data-testid={`flavor-sold-out-${f.id}`}>Sold out</span>}
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
